import React, { useState, useEffect, useCallback } from 'react';
import { Card, Dropdown } from 'react-bootstrap';
import LoadingOverlay from '../../common/LoadingOverlay';
import Pagination from '../../common/Pagination';
import { useTranslation } from 'react-i18next';

import api from '../../../api';

import ScanListItem from './ScanListItem/ScanListItem';

import exportAsCsv from '../../../utils/exportAsCsv';
import exportAsXlsx from '../../../utils/exportAsXlsx';

import usePagination from '../../../hooks/usePagination';

function ScanList(props) {
    const { t } = useTranslation();
    const { scanPointID } = props;
    const [ filteredScanRows, setFilteredScanRows ] = useState([]);
    const [ scanRows, setScanRows ] = useState([]);
    const [ loading, setLoading ] = useState(true);
    const [ limit, setLimit ] = useState(100);

    const {
		setPage,
		pageCount,
		currentPage,
		currentData,
    } = usePagination(filteredScanRows, limit);
    
    const currentScanRows = currentData();
    
     /**
     * Fetch scans.
     *
     * @param {string} search
     */
    const _fetchScans = useCallback((scanPointID) => {
        setLoading(true);
        api.scanpoint.getScans(scanPointID, {order: [{column: 'timeScanned', direction: 'desc'}]})
            .then(response => {
                const { scans } = response.data.data;
                setScanRows(scans);
                setFilteredScanRows(scans);
        })
        .finally(() => {
            setLoading(false);
        });

        return () => {
            setLoading(true);
            setScanRows([]);
            setFilteredScanRows([]);
        }
    }, []);


    /**
     * Handle search.
     *
     * @param {string} search
     */
    const _handleSearchChange = (event) => {
        const search = event.target.value;
        if(!search.length) {
            // Reset data if no search term
            setFilteredScanRows(scanRows);
        } else {
            // Filter scan rows
            const newRows = [...scanRows].filter(scan => {
                let scanAsString = Object.values(scan).filter(value => value).map(value => value.toLowerCase()).join(' ');
                return `/${scanAsString}/`.match(search.toLowerCase());
            });
            setFilteredScanRows(newRows);
        }
    };


    /**
     * Handle sort.
     *
     * @param {object} event
     */
    const _handleSortChange = (event) => {
        const [ key, direction ] = event.target.value.split('_');
        const desc = (direction === 'desc');
        const newRows = [...filteredScanRows].sort((a,b) => {
            if ( (desc && a[key] < b[key]) || (!desc && a[key] > b[key])) {
                return -1;
            }
            if ( desc && a[key] > b[key] ) {
                return 1;
            }
            return 0;
        });
        setFilteredScanRows(newRows);
    };

    /**
     * 
     *  Handle page limiting 
     */
    const _handleChangeLimit = ({ target }) => setLimit(Number(target.value));

    /**
     * Handle page change
     */
    const _onPageChange = ({ selected }) => setPage(selected + 1);

    /**
     * Handle export action.
     */
    const _handleExportScans = (fileType) => {
        if (fileType === 'xlsx') {
            api.scanpoint.downloadScansAsXlsx(scanPointID, fileType)
                .then((response) => exportAsXlsx(response, 'Scan activity'))
                .catch(error => console.log(error));
        } else {
            let csvData = [...filteredScanRows];
            const dataContainsEmail = csvData.some(scan => scan.email);
            const dataContainsPhone = csvData.some(scan => scan.phone);
            let columns = [ 
                { key: 'firstNames', label: 'First name' },
                { key: 'lastName', label: 'Last name' },
                { key: 'company', label: 'Company' },
                ...(dataContainsEmail ? [{ key: 'email', label: 'Email' }] : []),
                ...(dataContainsPhone ? [{ key: 'phone', label: 'Phone' }] : []),
                { key: 'comment', label: 'comment' },
                { key: 'timeScanned', label: 'Time scanned' }
            ];
            exportAsCsv(csvData, 'Scan activity', columns);
        }
    }

    /**
     * Fetch scans on component mount.
     */
    useEffect(() => {
        _fetchScans(scanPointID)
    }, [scanPointID, _fetchScans])

    return(
        <>
            <div className="container" style={{ fontSize: '14px' }}>
                <div className="row justify-content align-items-center">
                    <div className="col col-sm-auto">
                        <select className="form-control form-control-sm" defaultValue={limit} onChange={_handleChangeLimit}>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="200">200</option>
                            <option value="500">500</option>
                            <option value="1000">1000</option>
                        </select>
                    </div>
                    <div className="col col-sm-auto">
                        <select className="form-control form-control-sm" defaultValue="timeScanned_asc" onChange={_handleSortChange}>
                            <option value="timeScanned_asc">{t('general.newest_first')}</option>
                            <option value="timeScanned_desc">{t('general.oldest_first')}</option>
                            <option value="firstNames_desc">{t('general.first_name')} (A-Z)</option>
                            <option value="firstNames_asc">{t('general.first_name')} (Z-A)</option>
                            <option value="lastName_desc">{t('general.last_name')} (A-Z)</option>
                            <option value="lastName_asc">{t('general.last_name')} (Z-A)</option>
                            <option value="company_desc">Organisaatio</option>
                        </select>
                    </div>
                    <div className="col col-sm-auto">
                        <input type="text" className="form-control form-control-sm" placeholder={t('general.search')} onChange={_handleSearchChange} />
                    </div>
                    <div className="col">
                        {filteredScanRows.length} {t('general.rows')}
                    </div>
                    <div className="col text-right">
                        {/* <Button size="sm" onClick={_handleExportScans}>Lataa raportti </Button> */}
                        <Dropdown>
                            <Dropdown.Toggle size="sm" variant="primary" id="dropdown-basic">
                                {t('general.download_report')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => _handleExportScans('csv')}>CSV</Dropdown.Item>
                                <Dropdown.Item onClick={() => _handleExportScans('xlsx')}>Excel</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </div>

            <hr />

            <div className="DataList">
                {currentScanRows.map(scan =>
                    <ScanListItem scan={scan} key={scan.scanPointScanID} />
                )}
                {loading ?
                    <LoadingOverlay />
                : null}
            </div>

            {currentScanRows.length ?
                <div>
                    <div className="pull-right" style={{ padding: '.75rem 1.25rem' }}>
                        <Pagination
                            forcePage={currentPage}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={_onPageChange}
                        />
                    </div>
                </div>
            : null}
            {!currentScanRows.length && !loading ?
                <Card.Body>
                    <>{t('persons.no_guests')}</>
                </Card.Body>
            : null}
        </>
    )
}

export default ScanList;